// src/pages/offers/offers.components.jsx
import React from "react";
import { motion } from "framer-motion";

/* ========================= util UI (Iconos) ========================= */
export const EyeIcon = ({ className = "h-4 w-4" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12s-3.75 6.75-9.75 6.75S2.25 12 2.25 12Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 15.375A3.375 3.375 0 1 0 12 8.625a3.375 3.375 0 0 0 0 6.75Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ArrowUpTrayIcon = ({ className = "h-4 w-4" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M11.47 2.47a.75.75 0 0 1 1.06 0l4.5 4.5a.75.75 0 0 1-1.06 1.06l-3.22-3.22V16.5a.75.75 0 0 1-1.5 0V4.81L8.03 8.03a.75.75 0 0 1-1.06-1.06l4.5-4.5ZM3 19.5a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3v-8.25a.75.75 0 0 1 1.5 0v8.25a4.5 4.5 0 0 1-4.5 4.5H6a4.5 4.5 0 0 1-4.5-4.5v-8.25a.75.75 0 0 1 1.5 0v8.25Z"
      clipRule="evenodd"
    />
  </svg>
);

export const ReceiptIcon = ({ className = "h-4 w-4" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M7 2a2 2 0 0 0-2 2v17.5a.5.5 0 0 0 .8.4l1.7-1.3 1.7 1.3a.5.5 0 0 0 .6 0l1.7-1.3 1.7 1.3a.5.5 0 0 0 .6 0l1.7-1.3 1.7 1.3a.5.5 0 0 0 .8-.4V4a2 2 0 0 0-2-2H7zm2.25 6.25a.75.75 0 0 1 .75-.75h6a.75.75 0 0 1 0 1.5h-6a.75.75 0 0 1-.75-.75zm0 3a.75.75 0 0 1 .75-.75h6a.75.75 0 0 1 0 1.5h-6a.75.75 0 0 1-.75-.75zm0 3a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5H10a.75.75 0 0 1-.75-.75z" />
  </svg>
);

/* ========================= Estados de filtro ========================= */
export const STATUS_OPTIONS = [
  { value: "", label: "Todos los estados" },
  { value: "draft", label: "Cotizado" },
  { value: "sent", label: "Enviada" },
  { value: "accepted", label: "Aceptada" },
  { value: "billing", label: "Facturando" },
  { value: "closed", label: "Cerrada" },
  { value: "rejected", label: "Rechazada" },
];

export const symbolFor = (pc) =>
  String(pc || "").toUpperCase().includes("USD") ? "US$" : "$";

export function viaLabel(v) {
  switch (v) {
    case "oc":
      return "OC";
    case "email":
      return "Email";
    case "whatsapp":
      return "WhatsApp";
    case "phone":
      return "Teléfono";
    case "walkin":
      return "Traído";
    default:
      return v || "—";
  }
}

export function condTooltip(o) {
  const parts = [];
  if (o?.payment_method) parts.push(`Pago: ${o.payment_method}`);
  if (o?.delivery_place) parts.push(`Entrega: ${o.delivery_place}`);
  if (o?.delivery_time) parts.push(`Plazo: ${o.delivery_time}`);
  if (o?.price_currency) parts.push(`Moneda: ${o.price_currency}`);
  if (o?.validity_date) parts.push(`Validez: ${o.validity_date}`);
  if (o?.iva_rate != null) parts.push(`IVA: ${o.iva_rate}%`);
  return parts.join(" · ");
}

/* ========================= Componente de Acciones ========================= */
export const OfferActions = ({
  offer,
  onView,
  onEdit,
  onPdf,
  onAccept,
  onMarkSent,
  onMarkBilling,
  onMarkClosed,
  onReopen,
  onUploadOC,
  onRemitos,
  onHistory,
  onViewService,
  onReject,
}) => {
  const canReject = offer.status !== "accepted" && offer.status !== "rejected";
  const canMarkBilling =
    offer.status === "accepted" || offer.status === "billing" || offer.status === "closed";
  const canMarkClosed = offer.status === "accepted" || offer.status === "billing";
  const canReopen   = offer.status === "closed";
  const billingLabel = offer.status === "billing" ? "↩️ Seguir facturando" : "🧾 Facturar";

  return (
    <div className="flex flex-col gap-2 py-2">
      {/* Fila 1: Ver, Editar, PDF */}
      <div className="flex gap-2">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 px-2 py-1.5 text-xs rounded-md border border-slate-500/40 text-slate-300 hover:bg-slate-500/10 transition-colors inline-flex items-center justify-center gap-1"
          onClick={() => onView(offer)}
          title="Ver oferta"
        >
          <EyeIcon className="h-3.5 w-3.5" />
          Ver
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 px-2 py-1.5 text-xs rounded-md border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors"
          onClick={() => onEdit(offer)}
          title="Editar oferta"
        >
          ✏️ Editar
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 px-2 py-1.5 text-xs rounded-md border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors"
          onClick={() => onPdf(offer)}
          title="Previsualizar / Editar PDF"
        >
          📄 PDF
        </motion.button>
      </div>

      {/* Fila 2: Aceptar / Enviada / OC / REM */}
      <div className="flex gap-2">
        {offer.status !== "accepted" ? (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onAccept(offer)}
            className="flex-1 px-2 py-1.5 text-xs rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors"
            title="Marcar como Aceptada"
          >
            ✓ Aceptar
          </motion.button>
        ) : (
          <div className="flex-1 px-2 py-1.5 text-xs rounded-md bg-emerald-900/20 text-emerald-300 border border-emerald-700/40 text-center">
            ✓ Aceptada
          </div>
        )}

        {offer.status !== "sent" ? (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onMarkSent(offer)}
            className="flex-1 px-2 py-1.5 text-xs rounded-md border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 transition-colors"
            title={
              offer.status === "accepted"
                ? "Revertir a Enviada"
                : "Marcar como Enviada"
            }
          >
            📧 Enviada
          </motion.button>
        ) : (
          <div className="flex-1 px-2 py-1.5 text-xs rounded-md bg-blue-900/20 text-blue-300 border border-blue-700/40 text-center">
            📧 Enviada
          </div>
        )}

        {/* OC */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="px-2 py-1.5 text-xs rounded-md border border-orange-500/40 text-orange-400 hover:bg-orange-500/20 transition-colors inline-flex items-center gap-1"
          onClick={() => onUploadOC(offer)}
          title="Gestionar órdenes de compra"
        >
          <ArrowUpTrayIcon className="h-3 w-3" />
          OC
        </motion.button>

        {/* REM */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="px-2 py-1.5 text-xs rounded-md border border-amber-500/40 text-amber-300 hover:bg-amber-500/20 transition-colors inline-flex items-center gap-1"
          onClick={() => onRemitos(offer)}
          title="Subir / ver remitos escaneados"
        >
          <ReceiptIcon className="h-3 w-3" />
          REM
        </motion.button>
      </div>

      {/* Fila 3: Historial + Ver servicio */}
      <div className="flex gap-2">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 px-2 py-1.5 text-xs rounded-md border border-slate-600/40 text-slate-300 hover:bg-slate-600/10 transition-colors"
          onClick={() => onHistory(offer)}
          title="Ver historial de cambios"
        >
          📜 Historial
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 px-2 py-1.5 text-xs rounded-md border border-teal-500/40 text-teal-300 hover:bg-teal-500/10 transition-colors"
          onClick={() => onViewService(offer)}
          title="Ver servicio (precios por instrumento real)"
        >
          🔧 Ver servicio
        </motion.button>
      </div>

      {/* Fila 4: Facturando / Cerrada */}
      <div className="flex gap-2">
        {canMarkBilling ? (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 px-2 py-1.5 text-xs rounded-md border border-amber-500/60 text-amber-200 bg-amber-500/10 hover:bg-amber-500/20 transition-colors"
            onClick={() => onMarkBilling(offer)}
            title="Abrir la modal de facturación"
          >
            {billingLabel}
          </motion.button>
        ) : (
          <div className="flex-1" />
        )}

        {canMarkClosed ? (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 px-2 py-1.5 text-xs rounded-md border border-emerald-500/60 text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors"
            onClick={() => onMarkClosed(offer)}
            title="Marcar como Cerrada (ya facturada)"
          >
            🔒 Cerrada
          </motion.button>
        ) : canReopen ? (
          <div className="flex-1 flex gap-1.5">
            <div className="flex-1 px-2 py-1.5 text-xs rounded-md bg-zinc-900/25 text-zinc-200 border border-zinc-600/60 text-center">
              🔒 Cerrada
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-2 py-1.5 text-xs rounded-md border border-orange-500/50 text-orange-300 bg-orange-500/10 hover:bg-orange-500/20 transition-colors whitespace-nowrap"
              onClick={() => onReopen?.(offer)}
              title="Reabrir oferta — vuelve al estado Facturando"
            >
              ↩️ Reabrir
            </motion.button>
          </div>
        ) : (
          <div className="flex-1" />
        )}
      </div>

      {/* Fila 5: Rechazar */}
      <div className="flex gap-2">
        {canReject ? (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 px-2 py-1.5 text-xs rounded-md border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 transition-colors"
            onClick={() => onReject(offer)}
            title="Rechazar oferta"
          >
            ✗ Rechazar
          </motion.button>
        ) : (
          <div className="flex-1 px-2 py-1.5 text-xs rounded-md bg-rose-900/20 text-rose-300 border border-rose-700/40 text-center">
            ✗ Rechazada
          </div>
        )}
      </div>
    </div>
  );
};
